import { useQuery } from '@apollo/client';
import { mapRboConfigFields } from '@openx/components-deal/lib/utils/mapRboConfig';
import type { DealStatus, OAListDeal } from '@openx/types/deal';
import type { FetchDealsResponse, FetchPackageDealsProps } from '@openx/types/package/PackageHookTypes';
import { useSnackbar } from 'notistack';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GetDealsListDocument } from '../../../graphql/query/deals/GetDealsList.generated';

const defaultLimit = 50;
const defaultOffset = 0;

export const useDealsFetch = (packageUid: string | null, params: FetchPackageDealsProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { fetchAll } = params;
  const limit = params.limit ? params.limit : defaultLimit;
  const offset = params.offset ?? defaultOffset;

  const { loading, data, refetch } = useQuery(GetDealsListDocument, {
    skip: !packageUid,
    onError: () => {
      enqueueSnackbar(t('Failed to fetch deals'));
    },
    variables: {
      queryString: packageUid ? `package_uid=${encodeURIComponent(packageUid)}` : undefined,
    },
  });

  const deals: FetchDealsResponse<OAListDeal> = useMemo(() => {
    const objects = data?.deals?.objects ?? [];
    const dealsList = objects.map(dealRaw => ({
      ...dealRaw,
      _package: {
        audience: dealRaw._package?.audience ?? null,
        audience_id: dealRaw._package?.audience_id ?? null,
      },
      deal_id: dealRaw.exchange_deal_id ?? '',
      deal_participants: dealRaw.exchange_deal?.deal_participants ?? [],
      id: dealRaw.id ?? '',
      name: dealRaw.name ?? '',
      status: (dealRaw.status as DealStatus) ?? '',
      synchronized_with: dealRaw.exchange_deal?.synchronized_with,
      uid: dealRaw.exchange_uid ?? '',
      rbo_config: mapRboConfigFields(dealRaw.exchange_deal?.rbo_config),
    })) as OAListDeal[];

    if (fetchAll) {
      return {
        has_more: false,
        limit: dealsList.length,
        objects: dealsList,
        offset: 0,
      };
    }

    return {
      has_more: data?.deals?.has_more ?? false,
      limit: data?.deals?.limit ?? limit,
      objects: dealsList,
      offset: data?.deals?.offset ?? offset,
    };
  }, [data?.deals, limit, offset, fetchAll]);

  return {
    deals,
    dealsFetching: loading,
    dealsLoading: loading,
    refetchDeals: refetch,
  };
};
